import { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';

interface AdCreative {
  id: string;
  title: string;
  description: string;
  sponsor: string;
  ctaLabel: string;
  href?: string;
  imageUrl?: string;
  badge?: string;
  background?: string;
}

interface StartupAdOverlayProps {
  open: boolean;
  creative: AdCreative;
  countdownSeconds: number;
  onClose: () => void;
}

export default function StartupAdOverlay({ open, creative, countdownSeconds, onClose }: StartupAdOverlayProps) {
  const [remaining, setRemaining] = useState(countdownSeconds);
  const [imageFailed, setImageFailed] = useState(false);

  useEffect(() => {
    if (!open) return;
    setRemaining(countdownSeconds);
    setImageFailed(false);
  }, [open, countdownSeconds]);

  useEffect(() => {
    if (!open || remaining <= 0) {
      if (remaining <= 0 && open) onClose();
      return;
    }
    const timer = window.setTimeout(() => setRemaining((v) => v - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [open, remaining, onClose]);

  const progress = useMemo(() => {
    const done = countdownSeconds - remaining;
    return Math.max(0, Math.min(100, (done / countdownSeconds) * 100));
  }, [countdownSeconds, remaining]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/85 px-4 backdrop-blur-sm">
      <div className="relative w-full max-w-md overflow-hidden rounded-[28px] border border-white/15 shadow-2xl">
        <div className="absolute inset-0" style={{ background: creative.background || 'linear-gradient(145deg, #0f172a, #2563eb)' }} />
        {creative.imageUrl && !imageFailed && (
          <img src={creative.imageUrl} alt={creative.title} className="absolute inset-0 h-full w-full object-cover" onError={() => setImageFailed(true)} />
        )}
        <div className="relative flex min-h-[560px] flex-col justify-between bg-gradient-to-b from-slate-900/30 via-slate-900/15 to-slate-950/60 p-6 text-white">
          <div className="flex items-start justify-between">
            <div className="inline-flex items-center rounded-full bg-white/15 px-3 py-1 text-xs font-medium tracking-[0.18em] text-white/90">
              {creative.badge || '广告'}
            </div>
            <button type="button" onClick={onClose} className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-2 text-sm font-medium text-white transition hover:bg-white/25">
              <span>{remaining}s</span>
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="space-y-5">
            <div className="space-y-3">
              <p className="text-sm font-medium uppercase tracking-[0.28em] text-white/75">{creative.sponsor}</p>
              <h2 className="text-3xl font-bold leading-tight">{creative.title}</h2>
              <p className="max-w-[28ch] text-base leading-7 text-white/82">{creative.description}</p>
            </div>
            <div className="rounded-[22px] border border-white/15 bg-white/10 p-4 backdrop-blur-sm">
              <p className="text-sm text-white/80">广告加载失败也不会阻塞进入，倒计时结束会自动关闭。</p>
            </div>
          </div>
          <div className="space-y-4">
            {creative.href ? (
              <a href={creative.href} onClick={onClose} className="inline-flex w-full items-center justify-center rounded-[20px] bg-white px-4 py-4 text-base font-semibold text-slate-950 transition hover:bg-slate-100">
                {creative.ctaLabel}
              </a>
            ) : (
              <button type="button" onClick={onClose} className="inline-flex w-full items-center justify-center rounded-[20px] bg-white px-4 py-4 text-base font-semibold text-slate-950 transition hover:bg-slate-100">
                {creative.ctaLabel}
              </button>
            )}
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/15">
              <div className="h-full rounded-full bg-white transition-[width] duration-500" style={{ width: `${progress}%` }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
